import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ActivityIndicator, Image, Modal, Platform, ScrollView, Share, StyleSheet, TextInput, TouchableOpacity, View, Linking, Alert } from 'react-native';
import Constants from 'expo-constants';
import * as Application from 'expo-application';
import * as Updates from 'expo-updates';
import * as Clipboard from 'expo-clipboard';
import { Text } from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import QRCode from 'react-native-qrcode-svg';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { useAppColors } from '../../hooks/useAppColors';
import { responsiveContentStyle, WEB_MAX_WIDTH } from '../../hooks/useResponsiveLayout';
import { useAuthStore } from '../../stores/authStore';
import { useRoleStore } from '../../stores/roleStore';
import { useThemeStore } from '../../stores/themeStore';
import { useFamilyMemberships, useActiveMembership, useMyMemberships, useMembershipPlans, usePlanCatalog } from '../../hooks/usePayments';
import { useGuardians, useInviteSubUser } from '../../hooks/useFamilies';
import { useLogout, useUpdateProfile, useCurrentUser } from '../../hooks/useAuth';
import { useMyReferral } from '../../hooks/useReferral';
import { usePickupPersons } from '../../hooks/usePickupPersons';
import LegalDocumentsSection from '../legal/LegalDocumentsSection';
import DatePicker from '../../components/DatePicker';
import { useProfilePicture } from '../../hooks/useProfilePicture';
import { getProfilePictureUrl } from '../../api/users';
import AppHeader from '../../components/common/AppHeader';
import LoadingIndicator from '../../components/common/LoadingIndicator';
import { formatDate, formatCurrency, calculateAge, getApiError, kgToLbs, lbsToKg, cmToIn, inToCm, weightToKg, heightToCm, displayWeight, displayHeight } from '../../utils/formatters';
import { useTranslation } from 'react-i18next';
import { changeLanguage, SUPPORTED_LANGUAGES, type SupportedLanguage } from '../../i18n';
import { getDiscountText, calculateOfferPrice, getMemberOffers, getOfferForPlanTerm } from '../../components/billing/billingUtils';

export default function ProfileScreen() {
  const { t, i18n } = useTranslation();
  const colors = useAppColors();
  const user = useAuthStore((s) => s.user);
  const activeRole = useRoleStore((s) => s.activeRole);

  // Debug: log user profile picture URL
  console.log('ProfileScreen user:', { id: user?.id, profile_picture_url: user?.profile_picture_url });
  const activeProfile = useRoleStore((s) => s.activeProfile);
  const themeMode = useThemeStore((s) => s.mode);
  const toggleTheme = useThemeStore((s) => s.toggleTheme);
  const logout = useLogout();
  const navigation = useNavigation<any>();
  const userId = user?.id || '';
  const [showAppSettings, setShowAppSettings] = useState(false);
  const [showLanguagePicker, setShowLanguagePicker] = useState(false);
  const [showProfilePictureMenu, setShowProfilePictureMenu] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  // Action to run once the picture-menu modal has fully closed. Launching the
  // native image picker while our own modal is still dismissing fails silently
  // on iOS (two modals can't present at once) — the upload spinner then hangs.
  const pendingPictureAction = useRef<null | (() => void)>(null);
  const runPendingPictureAction = () => {
    const action = pendingPictureAction.current;
    pendingPictureAction.current = null;
    if (action) action();
  };
  // Same deferral for the profile menu -> opening another native modal (the
  // personal-details editor) while this one dismisses fails/flickers on iOS.
  const pendingProfileMenuAction = useRef<null | (() => void)>(null);
  const runPendingProfileMenuAction = () => {
    const action = pendingProfileMenuAction.current;
    pendingProfileMenuAction.current = null;
    if (action) action();
  };
  const [showPersonalModal, setShowPersonalModal] = useState(false);
  const [personalFirst, setPersonalFirst] = useState('');
  const [personalLast, setPersonalLast] = useState('');
  const [personalDob, setPersonalDob] = useState<Date | null>(null);
  const [personalError, setPersonalError] = useState('');
  const [showBodyModal, setShowBodyModal] = useState(false);
  const [weightInput, setWeightInput] = useState('');
  const [heightInput, setHeightInput] = useState('');
  const [unitSystem, setUnitSystem] = useState<'metric' | 'imperial'>(
    (user?.measurement_unit as 'metric' | 'imperial') || 'metric'
  );
  const updateProfile = useUpdateProfile();
  const { data: freshUserData, refetch: refetchUser } = useCurrentUser();
  const updateUser = useAuthStore((s) => s.updateUser);

  // Refresh user data from backend when screen mounts (for admin changes, etc.)
  useEffect(() => {
    if (freshUserData) {
      updateUser(freshUserData);
    }
  }, [freshUserData, updateUser]);

  // Force refetch when screen comes into focus (to catch admin updates)
  useFocusEffect(
    React.useCallback(() => {
      refetchUser();
    }, [refetchUser])
  );

  const { isUploading, pickAndUploadImage, deleteImage } = useProfilePicture();

  const { data: familyMemberships } = useFamilyMemberships();
  const { data: activeMembershipData } = useActiveMembership();
  const { data: myMemberships } = useMyMemberships();
  const { data: plans } = useMembershipPlans();
  const { data: myGuardians, isLoading: isLoadingGuardians } = useGuardians(userId);
  const { data: pickupPersons } = usePickupPersons();

  // Collect all user IDs from family memberships for catalog fetch (include current user)
  const allMemberIds = useMemo(() => {
    const ids: string[] = [];
    if (user) ids.push(user.id);
    familyMemberships?.forEach((fm: any) => {
      if (fm.user_id && user && fm.user_id !== user.id) ids.push(fm.user_id);
    });
    return ids;
  }, [user, familyMemberships]);
  const { data: catalogData } = usePlanCatalog(allMemberIds.length > 0 ? allMemberIds : null);
  const catalogEntries = catalogData?.entries ?? [];
  const inviteSubUser = useInviteSubUser();
  const pendingVerificationPaymentId = useAuthStore((s) => s.pendingVerificationPaymentId);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showLegalDocuments, setShowLegalDocuments] = useState(false);

  // Referral data
  const { data: referralData } = useMyReferral();
  const [copiedCode, setCopiedCode] = useState(false);

  // Invite-to-app modal state
  const [inviteTarget, setInviteTarget] = useState<{ id: string; name: string; existingEmail?: string | null } | null>(null);
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [inviteExpires, setInviteExpires] = useState<string | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteCopied, setInviteCopied] = useState(false);

  if (!user) return <LoadingIndicator />;

  // A user is a managed sub-user if at least one guardian is linked to them.
  const isManagedSubUser = !!myGuardians && myGuardians.length > 0;
  const primaryGuardian = isManagedSubUser ? myGuardians![0].guardian : undefined;
  const primaryGuardianName = primaryGuardian
    ? `${primaryGuardian.first_name} ${primaryGuardian.last_name}`
    : undefined;

  const isGuardian = !isManagedSubUser && (activeRole === 'guardian' || activeRole === 'member');
  const hasDependents = familyMemberships && familyMemberships.length > 1;
  const athleteName = activeProfile?.name || `${user.first_name} ${user.last_name}`;
  const guardianName = isGuardian
    ? `${user.first_name} ${user.last_name}`
    : undefined;

  const openInviteModal = (member: { user_id: string; user_name: string; email?: string | null }) => {
    setInviteTarget({ id: member.user_id, name: member.user_name, existingEmail: member.email });
    setInviteToken(null);
    setInviteUrl(null);
    setInviteExpires(null);
    setInviteError(null);
    setInviteCopied(false);
    // Generate invite token on modal open
    inviteSubUser.mutate(
      { athlete_id: member.user_id, regenerate: false },
      {
        onSuccess: (data) => {
          setInviteToken(data.token);
          setInviteUrl(data.url);
          setInviteExpires(data.expires_at);
        },
        onError: (err) => setInviteError(getApiError(err, t('profile.invite.failed'), t)),
      }
    );
  };

  const closeInviteModal = () => {
    setInviteTarget(null);
    setInviteToken(null);
    setInviteUrl(null);
    setInviteExpires(null);
    setInviteError(null);
    setInviteCopied(false);
  };

  const copyInviteUrl = async () => {
    if (!inviteUrl) return;
    try {
      await Clipboard.setStringAsync(inviteUrl);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const truncateUrl = (url: string, max = 48) => {
    if (url.length <= max) return url;
    const head = Math.ceil((max - 1) / 2);
    const tail = Math.floor((max - 1) / 2);
    return `${url.slice(0, head)}…${url.slice(url.length - tail)}`;
  };

  const regenerateInvite = () => {
    if (!inviteTarget) return;
    setInviteError(null);
    inviteSubUser.mutate(
      { athlete_id: inviteTarget.id, regenerate: true },
      {
        onSuccess: (data) => {
          setInviteToken(data.token);
          setInviteUrl(data.url);
          setInviteExpires(data.expires_at);
        },
        onError: (err) => setInviteError(getApiError(err, t('profile.invite.failed'), t)),
      }
    );
  };

  // Referral code helpers
  const copyReferralCode = async () => {
    if (!referralData?.my_code.code) return;
    try {
      await Clipboard.setStringAsync(referralData.my_code.code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    } catch {
      // ignore
    }
  };

  const shareReferralCode = async () => {
    if (!referralData?.my_code.code) return;
    try {
      const message = t('referral.shareMessage', { code: referralData.my_code.code });
      if (Platform.OS === 'web') {
        if (navigator.share) {
          await navigator.share({ title: t('referral.title'), text: message });
        } else {
          await Clipboard.setStringAsync(message);
          setCopiedCode(true);
          setTimeout(() => setCopiedCode(false), 2000);
        }
      } else {
        await Share.share({ message });
      }
    } catch {
      // ignore
    }
  };


  return (
    <View style={[styles.screen, { backgroundColor: colors.bg }]}>
      <AppHeader />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[styles.content, responsiveContentStyle]}
        showsVerticalScrollIndicator={false}
      >
        {/* Profile Info */}
        <View style={styles.profileRow}>
          <View style={styles.avatarWrapper}>
            <TouchableOpacity
              style={[
                styles.avatar,
                { backgroundColor: colors.card, borderColor: colors.border },
              ]}
              onPress={() => setShowProfilePictureMenu(true)}
              activeOpacity={0.7}
              disabled={isUploading}
            >
              {user.profile_picture_url ? (
                <Image
                  source={{ uri: (() => {
                    // Use updated_at as cache-buster since profile picture path is deterministic
                    // (same URL after delete + re-upload would show cached old image otherwise)
                    const url = getProfilePictureUrl(user.profile_picture_url, user.updated_at);
                    console.log('Profile picture URL:', { original: user.profile_picture_url, constructed: url });
                    return url;
                  })() }}
                  style={styles.avatarImage}
                />
              ) : (
                <Text style={styles.avatarEmoji}>
                  {isGuardian ? '\uD83D\uDC66' : '\uD83E\uDD4B'}
                </Text>
              )}
              {isUploading && (
                <View style={styles.avatarOverlay}>
                  <ActivityIndicator size="small" color="#fff" />
                </View>
              )}
            </TouchableOpacity>
            <View style={styles.cameraIconOverlay} pointerEvents="none">
              <Icon name="camera" size={14} color="rgba(255,255,255,0.9)" />
            </View>
          </View>
          <View style={styles.profileInfo}>
            <Text style={[styles.profileName, { color: colors.text }]}>
              {athleteName}
            </Text>
            {guardianName && (
              <Text style={[styles.profileSub, { color: colors.textSecondary }]}>
                {t('profile.guardian', { name: guardianName })}
              </Text>
            )}
            {user.dob && (
                <Text style={[styles.profileSub, { color: colors.textSecondary }]}>
                  {t('profile.age', { count: calculateAge(user.dob) })}
                </Text>
            )}
          </View>
          <TouchableOpacity
            onPress={() => setShowProfileMenu(true)}
            activeOpacity={0.6}
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          >
            <Icon name="dots-vertical" size={20} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>

        {/* Membership & Billing Summary Card */}
        <View style={styles.section}>
          <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>
            {t('profile.membershipPayment')}
          </Text>
          <View style={[styles.summaryCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
            {/* Pending activation banner */}
            {pendingVerificationPaymentId && !(activeMembershipData?.has_active_membership) ? (
              <View style={[styles.pendingActivationBanner, { backgroundColor: colors.warning + '15', borderColor: colors.warning }]}>
                <Icon name="clock-check-outline" size={20} color={colors.warning} />
                <View style={{ flex: 1, marginLeft: 10 }}>
                  <Text style={[styles.pendingActivationTitle, { color: colors.text }]}>
                    {t('profile.pendingActivation.title')}
                  </Text>
                  <Text style={[styles.pendingActivationBody, { color: colors.textSecondary }]}>
                    {t('profile.pendingActivation.body')}
                  </Text>
                </View>
              </View>
            ) : (
              <>
                {/* Current plan */}
                {(() => {
                  const activeMember = activeMembershipData?.members?.find((member: any) => member.user_id === userId);
                  const activeMembership = activeMember?.membership;
                  const expiredMembership = myMemberships?.find((m: any) => m.user_id === userId && m.status === 'expired');
                  const userFullName = `${user.first_name} ${user.last_name}`;
                  const primaryMembership = (activeMembership?.status === 'active' || activeMembership?.status === 'paused')
                    ? activeMembership
                    : expiredMembership || null;
                  const members = [
                    { user_id: userId, user_name: userFullName, membership: primaryMembership },
                    ...(familyMemberships || []).filter((fm: any) => fm.user_id !== userId),
                  ];
                  const totalMonthly = members.reduce((sum: number, member: any) => {
                    const membership = member.membership;
                    if (membership?.status !== 'active' || !membership?.plan?.price || !membership?.renewal_term_months) return sum;
                    // Use catalog-aware pricing for total calculation
                    const memberOffers = getMemberOffers(catalogEntries, member.user_id);
                    const offer = getOfferForPlanTerm(memberOffers, membership.plan_id, membership.renewal_term_months);
                    const pricing = membership.plan && membership.renewal_term_months
                      ? calculateOfferPrice(membership.plan, membership.renewal_term_months, offer)
                      : null;
                    const amount = membership.billing_details?.line_amount != null
                      ? parseFloat(membership.billing_details.line_amount)
                      : pricing?.finalAmount || 0;
                    return sum + amount;
                  }, 0);

                  return (
                    <>
                      {members.map((member: any, index: number) => {
                        const membership = member.membership;
                        const plan = membership?.plan;
                        const status = membership?.status;
                        const isLastMember = index === members.length - 1;
                        const isPaused = status === 'paused';
                        const statusColor = status === 'active' ? '#22c55e' : status === 'expired' ? '#ef4444' : isPaused ? colors.warning : colors.textSecondary;
                        const statusBackground = status === 'active'
                          ? 'rgba(34,197,94,0.12)'
                          : status === 'expired'
                            ? 'rgba(239,68,68,0.12)'
                            : isPaused
                              ? 'rgba(245,158,11,0.12)'
                              : colors.border;

                        // Calculate pricing to show discount using catalog-aware pricing
                        const memberOffers = getMemberOffers(catalogEntries, member.user_id);
                        const offer = getOfferForPlanTerm(memberOffers, membership?.plan_id, membership?.renewal_term_months);
                        const pricing = plan && membership?.renewal_term_months
                          ? calculateOfferPrice(plan, membership.renewal_term_months, offer)
                          : null;
                        const hasSavings = pricing ? pricing.discountAmount > 0 : false;
                        const finalAmount = membership?.billing_details?.line_amount != null
                          ? parseFloat(membership.billing_details.line_amount)
                          : pricing?.finalAmount || 0;

                        return (
                          <View key={`summary-${member.user_id}`}>
                            <View style={styles.summaryRow}>
                              <View style={{ flex: 1, gap: 2 }}>
                                <Text style={[styles.summaryLabel, { color: colors.textSecondary }]} numberOfLines={1}>
                                  {member.user_name}
                                </Text>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                  <Text style={[styles.summaryPlanName, { color: plan ? colors.text : colors.textTertiary }]} numberOfLines={1}>
                                    {plan?.machine_name
                                      ? t(`plans.${plan.machine_name}.name`, { defaultValue: plan.machine_name })
                                      : membership?.plan_id
                                        ? t('profile.currentPlan')
                                        : t('profile.noPlan')}
                                  </Text>
                                  {isPaused && (
                                    <View style={[styles.pausedBadge, { backgroundColor: colors.warning + '20', borderColor: colors.warning }]}
                                      testID="paused-badge-main">
                                      <Icon name="pause-circle" size={10} color={colors.warning} />
                                      <Text style={[styles.pausedBadgeText, { color: colors.warning }]}>
                                        {t('membershipStatus.paused', 'Paused')}
                                      </Text>
                                    </View>
                                  )}
                                </View>
                                {hasSavings && pricing?.discount && (
                                  <Text style={[styles.summaryDiscount, { color: colors.accent }]}>
                                    {getDiscountText(pricing.discount, pricing.subtotal, pricing.finalAmount, formatCurrency, t, membership.renewal_term_months || 0)}
                                  </Text>
                                )}
                              </View>
                              <View style={styles.planMeta}>
                                {status && !isPaused && (
                                  <View style={[styles.activeBadge, { backgroundColor: statusBackground }]}>
                                    <View style={[styles.activeDot, { backgroundColor: statusColor }]} />
                                    <Text style={{ color: statusColor, fontSize: 10, fontWeight: '700' }}>
                                      {status === 'active'
                                        ? t('profile.active')
                                        : t(`membershipStatus.${status}`, { defaultValue: status })}
                                    </Text>
                                  </View>
                                )}
                                {isPaused && membership?.active_pause && (
                                  <Text style={[styles.summaryMetaValue, { color: colors.textSecondary, fontSize: 11 }]}>
                                    {membership.active_pause.paused_end_date
                                      ? t('profile.pausedUntil', { date: formatDate(membership.active_pause.paused_end_date, i18n.language) })
                                      : t('profile.pausedIndefinite')}
                                  </Text>
                                )}
                                {pricing && membership?.renewal_term_months && !(isManagedSubUser && member.user_id === userId) && (
                                  <View style={styles.priceCol}>
                                    {hasSavings && pricing && (
                                      <Text style={[styles.priceStrike, { color: colors.textTertiary }]}>
                                        {formatCurrency(pricing.subtotal)}
                                      </Text>
                                    )}
                                    <Text style={[styles.summaryValue, { color: colors.text }]}>
                                      {formatCurrency(finalAmount)} / {membership.renewal_term_months} {membership.renewal_term_months > 1 ? 'months' : 'month'}
                                    </Text>
                                  </View>
                                )}
                                {membership?.start_date && (
                                  <Text style={[styles.summaryMetaValue, { color: colors.textTertiary }]}>
                                    {t('profile.started', { date: formatDate(membership.start_date, i18n.language) })}
                                  </Text>
                                )}
                                {membership?.start_date && membership?.renewal_term_months && (
                                  <Text style={[styles.summaryMetaValue, { color: colors.textTertiary }]}>
                                    {t('profile.ends', { date: formatDate(
                                      new Date(new Date(membership.start_date).setMonth(
                                        new Date(membership.start_date).getMonth() + membership.renewal_term_months
                                      )).toISOString(),
                                      i18n.language
                                    ) })}
                                  </Text>
                                )}
                              </View>
                            </View>
                            {!isLastMember && <View style={[styles.summaryDivider, { borderBottomColor: colors.border }]} />}
                          </View>
                        );
                      })}
                      {totalMonthly > 0 && (
                        <>
                          <View style={[styles.summaryDivider, { borderBottomColor: colors.border }]} />
                          <View style={styles.summaryRow}>
                            <Text style={[styles.totalLabel, { color: colors.text }]}>{t('billing.total')}</Text>
                            <Text style={[styles.totalValue, { color: colors.text }]}>
                              {formatCurrency(totalMonthly)}
                            </Text>
                          </View>
                        </>
                      )}
                    </>
                  );
                })()}

                {/* Managed-by banner OR Manage button */}
                {isLoadingGuardians ? (
                  <View style={[styles.managedBanner, { backgroundColor: colors.bg, borderColor: colors.border }]}>
                    <ActivityIndicator size="small" color={colors.textSecondary} />
                  </View>
                ) : isManagedSubUser ? (
                  <View style={[styles.managedBanner, { backgroundColor: colors.bg, borderColor: colors.border }]}>
                    <Icon name="shield-account" size={16} color={colors.textSecondary} />
                    <Text style={[styles.managedBannerText, { color: colors.textSecondary }]}>
                      {primaryGuardianName
                        ? t('profile.managedBy', { name: primaryGuardianName })
                        : t('profile.managedByFamily')}
                    </Text>
                  </View>
                ) : (
                  <TouchableOpacity
                    style={[styles.manageBtn, { backgroundColor: colors.accent }]}
                    onPress={() => (navigation as any).navigate('MembershipManagement')}
                    activeOpacity={0.7}
                  >
                    <Icon name="card-account-details" size={18} color={colors.accentText} />
                    <Text style={[styles.manageBtnText, { color: colors.accentText }]}>{t('profile.manageMembership')}</Text>
                  </TouchableOpacity>
                )}
              </>
            )}
          </View>
        </View>

        {/* Family Members & Memberships */}
        {hasDependents && familyMemberships && familyMemberships.length > 0 && (
          <View style={styles.section}>
            <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>
              {t('profile.familyMembers')}
            </Text>
            <View style={[styles.familyCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
              {familyMemberships.map((fm: any, index: number) => {
                const isSelf = fm.user_id === userId;
                const isViewing = activeProfile?.id === fm.user_id;
                const isLast = index === familyMemberships.length - 1;
                const hasEmail = !!fm.email;
                const isActivated = hasEmail && fm.terms_accepted_at != null;
                const canInvite = !isSelf && !isActivated; // guardian can invite any non-self family member without a login who hasn't activated yet
                return (
                  <View key={fm.user_id}>
                    <View style={styles.memberRow}>
                      <View style={[styles.childAvatar, { backgroundColor: colors.border }]}>
                        <Icon name={isSelf ? 'account' : 'account-child'} size={20} color={colors.textSecondary} />
                      </View>
                      <View style={styles.childInfo}>
                        <Text style={[styles.childName, { color: colors.text }]}>
                          {fm.user_name}
                        </Text>
                        {fm.membership ? (
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <Text style={[styles.childMeta, { color: colors.textTertiary }]}>
                              {fm.membership.plan?.machine_name
                                ? t(`plans.${fm.membership.plan.machine_name}.name`, { defaultValue: fm.membership.plan.machine_name })
                                : fm.membership.plan_id
                                  ? t('profile.currentPlan')
                                  : t('profile.noPlan')}
                            </Text>
                            {fm.membership?.status === 'paused' && (
                              <View style={[styles.pausedBadge, { backgroundColor: colors.warning + '20', borderColor: colors.warning }]}
                                testID="paused-badge">
                                <Icon name="pause-circle" size={10} color={colors.warning} />
                                <Text style={[styles.pausedBadgeText, { color: colors.warning }]}>
                                  {t('membershipStatus.paused', 'Paused')}
                                </Text>
                              </View>
                            )}
                            {fm.membership?.status === 'paused' && fm.membership?.active_pause && (
                              <Text style={[styles.childMeta, { color: colors.textSecondary, fontSize: 11 }]}>
                                {fm.membership.active_pause.paused_end_date
                                  ? t('profile.pausedUntil', { date: formatDate(fm.membership.active_pause.paused_end_date) })
                                  : t('profile.pausedIndefinite')}
                              </Text>
                            )}
                          </View>
                        ) : (
                          <Text style={[styles.childMeta, { color: colors.textTertiary }]}>
                            {t('profile.noPlan')}
                          </Text>
                        )}
                        {fm.dob && (
                          <Text style={[styles.childMeta, { color: colors.textTertiary }]}>
                            {t('profile.age', { count: calculateAge(fm.dob) })}
                          </Text>
                        )}
                        {(fm.weight_kg || fm.height_cm) && (
                          <Text style={[styles.childMeta, { color: colors.textTertiary }]}>
                            <Icon name="ruler" size={11} color={colors.textTertiary} />
                            {' '}
                            {fm.weight_kg && `${Math.round(fm.weight_kg)}kg`}
                            {fm.weight_kg && fm.height_cm && ' / '}
                            {fm.height_cm && `${Math.round(fm.height_cm)}cm`}
                          </Text>
                        )}
                        {!isSelf && hasEmail && (
                          <Text style={[styles.childMeta, { color: colors.textTertiary }]} numberOfLines={1}>
                            <Icon name="email-outline" size={11} color={colors.textTertiary} />
                            {' '}{fm.email}
                          </Text>
                        )}
                      </View>
                      {isSelf && hasDependents && (
                        <View style={[styles.guardianBadge, { backgroundColor: colors.badgeBg }]}>
                          <Text style={[styles.guardianBadgeText, { color: colors.badgeText }]}>
                            {t('profile.guardianTag')}
                          </Text>
                        </View>
                      )}
                      {isViewing && !isSelf && (
                        <View style={[styles.viewingBadge, { backgroundColor: colors.border }]}>
                          <Text style={[styles.viewingBadgeText, { color: colors.textSecondary }]}>
                            {t('profile.viewing')}
                          </Text>
                        </View>
                      )}
                      {!isSelf && (
                        isActivated ? (
                          <View style={[styles.inviteBtn, { borderColor: colors.border, backgroundColor: colors.border }]}>
                            <Icon name="check-circle" size={14} color={colors.textSecondary} />
                            <Text style={[styles.inviteBtnText, { color: colors.textSecondary }]}>
                              {t('profile.invite.registered')}
                            </Text>
                          </View>
                        ) : (
                          <TouchableOpacity
                            onPress={() => openInviteModal(fm)}
                            style={[styles.inviteBtn, { borderColor: colors.border, backgroundColor: hasEmail ? 'transparent' : colors.accent }]}
                            activeOpacity={0.7}
                          >
                            <Icon
                              name={hasEmail ? 'pencil-outline' : 'email-plus-outline'}
                              size={14}
                              color={hasEmail ? colors.textSecondary : colors.accentText}
                            />
                            <Text style={[styles.inviteBtnText, { color: hasEmail ? colors.textSecondary : colors.accentText }]}>
                              {hasEmail ? t('profile.invite.edit') : t('profile.invite.invite')}
                            </Text>
                          </TouchableOpacity>
                        )
                      )}
                    </View>
                    {!isLast && <View style={[styles.memberDivider, { borderBottomColor: colors.border }]} />}
                  </View>
                );
              })}
            </View>
          </View>
        )}

        {/* Pickup Persons Section */}
        {hasDependents && (
          <View style={styles.section}>
            <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>
              {t('pickup.sectionLabel')}
            </Text>
            <TouchableOpacity
              style={[styles.manageBtn, { backgroundColor: colors.card, borderColor: colors.border, borderWidth: 1 }]}
              onPress={() => navigation.navigate('PickupPersons')}
              activeOpacity={0.7}
            >
              <Icon name="car-child-seat" size={18} color={colors.text} />
              <Text style={[styles.manageBtnText, { color: colors.text }]}>
                {t('pickup.manage', { count: pickupPersons?.length ?? 0 })}
              </Text>
              <Icon name="chevron-right" size={18} color={colors.textSecondary} />
            </TouchableOpacity>
          </View>
        )}

        {/* Referral Section */}
        {referralData && (
          <View style={styles.section}>
            <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>
              {t('referral.title')}
            </Text>
            <View style={[styles.referralCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
              {/* Referrer's Code */}
              <View style={styles.referralHeader}>
                <Icon name="gift-outline" size={20} color={colors.accent} />
                <Text style={[styles.referralTitle, { color: colors.text }]}>
                  {t('referral.subtitle')}
                </Text>
              </View>
              <Text style={[styles.referralDesc, { color: colors.textSecondary }]}>
                {t('referral.howItWorks')}
              </Text>

              {/* Code Display */}
              {!referralData.my_code.is_revoked ? (
                <>
                  <View style={styles.codeRow}>
                    <View style={[styles.codePill, { backgroundColor: colors.border }]}>
                      <Text style={[styles.codeText, { color: colors.text, fontFamily: 'monospace' }]}>
                        {referralData.my_code.code}
                      </Text>
                    </View>
                    <TouchableOpacity
                      style={[styles.codeActionBtn, { backgroundColor: colors.accent }]}
                      onPress={copyReferralCode}
                    >
                      <Icon name={copiedCode ? 'check' : 'content-copy'} size={16} color={colors.accentText} />
                      <Text style={[styles.codeActionText, { color: colors.accentText }]}>
                        {copiedCode ? t('referral.copied') : t('referral.copy')}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.codeActionBtn, { backgroundColor: colors.border }]}
                      onPress={shareReferralCode}
                    >
                      <Icon name="share-variant" size={16} color={colors.text} />
                      <Text style={[styles.codeActionText, { color: colors.text }]}>
                        {t('referral.share')}
                      </Text>
                    </TouchableOpacity>
                  </View>


                </>
              ) : (
                <View style={[styles.revokedBanner, { backgroundColor: colors.error + '15' }]}>
                  <Icon name="close-circle" size={16} color={colors.error} />
                  <Text style={[styles.revokedText, { color: colors.error }]}>
                    {t('referral.status.codeRevoked')}
                  </Text>
                </View>
              )}

              {/* Redemptions List */}
              {referralData.my_code.redemptions.length > 0 && (
                <View style={styles.redemptionsList}>
                  <Text style={[styles.redemptionsTitle, { color: colors.textSecondary }]}>
                    {t('referral.redemptionsTitle')}
                  </Text>
                  {referralData.my_code.redemptions.map((redemption, index) => (
                    <View key={index} style={[styles.redemptionItem, { borderBottomColor: colors.border }]}>
                      <View style={styles.redemptionHeader}>
                        <Text style={[styles.redemptionName, { color: colors.text }]}>
                          {redemption.referee_name}
                        </Text>
                        {redemption.referrer_rewarded_at ? (
                          <View style={[styles.rewardBadge, { backgroundColor: 'rgba(34,197,94,0.12)' }]}>
                            <Icon name="check" size={12} color="#22c55e" />
                            <Text style={[styles.rewardBadgeText, { color: '#22c55e' }]}>
                              {t('referral.rewardStatus.awarded')}
                            </Text>
                          </View>
                        ) : redemption.referrer_skipped_reason ? (
                          <View style={[styles.rewardBadge, { backgroundColor: colors.error + '15' }]}>
                            <Icon name="close" size={12} color={colors.error} />
                            <Text style={[styles.rewardBadgeText, { color: colors.error }]}>
                              {t('referral.rewardStatus.skipped')}
                            </Text>
                          </View>
                        ) : (
                          <View style={[styles.rewardBadge, { backgroundColor: colors.warning + '15' }]}>
                            <Icon name="clock-outline" size={12} color={colors.warning} />
                            <Text style={[styles.rewardBadgeText, { color: colors.warning }]}>
                              {t('referral.rewardStatus.pending')}
                            </Text>
                          </View>
                        )}
                      </View>
                      <Text style={[styles.redemptionDate, { color: colors.textTertiary }]}>
                        {t('referral.redeemedOn', { date: formatDate(redemption.redeemed_at, i18n.language) })}
                        {redemption.reward_due_at && (
                          <> • {t('referral.rewardDue', { date: formatDate(redemption.reward_due_at, i18n.language) })}</>
                        )}
                      </Text>
                    </View>
                  ))}
                </View>
              )}

              {/* Referee Status Block */}
              {referralData.redeemed_as_referee && (
                <View style={[styles.refereeBox, { borderTopColor: colors.border }]}>
                  <Text style={[styles.refereeTitle, { color: colors.textSecondary }]}>
                    {t('referral.refereeTitle')}
                  </Text>
                  {!referralData.redeemed_as_referee.rewarded_at && !referralData.redeemed_as_referee.skipped_reason && (
                    <View style={[styles.statusBox, { backgroundColor: colors.border }]}>
                      <Icon name="clock-outline" size={16} color={colors.warning} />
                      <Text style={[styles.statusBoxText, { color: colors.text }]}>
                        {t('referral.status.refereePending', {
                          name: referralData.redeemed_as_referee.referrer_name,
                          date: formatDate(referralData.redeemed_as_referee.redeemed_at, i18n.language),
                          rewardDate: formatDate(referralData.redeemed_as_referee.reward_due_at, i18n.language),
                        })}
                      </Text>
                    </View>
                  )}
                  {referralData.redeemed_as_referee.rewarded_at && (
                    <View style={[styles.statusBox, { backgroundColor: 'rgba(34,197,94,0.12)', borderColor: 'rgba(34,197,94,0.3)' }]}>
                      <Icon name="check-circle" size={16} color="#22c55e" />
                      <Text style={[styles.statusBoxText, { color: colors.text }]}>
                        {t('referral.status.refereeAwarded', {
                          name: referralData.redeemed_as_referee.referrer_name,
                          date: formatDate(referralData.redeemed_as_referee.rewarded_at, i18n.language),
                        })}
                      </Text>
                    </View>
                  )}
                  {referralData.redeemed_as_referee.skipped_reason && (
                    <View style={[styles.statusBox, { backgroundColor: colors.border }]}>
                      <Icon name="information-outline" size={16} color={colors.textTertiary} />
                      <Text style={[styles.statusBoxText, { color: colors.textSecondary }]}>
                        {t('referral.status.refereeForfeited')}
                      </Text>
                    </View>
                  )}
                </View>
              )}
            </View>
          </View>
        )}

        {/* Settings Links */}
        <View style={styles.linkList}>
          <TouchableOpacity
            style={[
              styles.linkItem,
              { backgroundColor: colors.card, borderColor: colors.border },
            ]}
            onPress={() => {
              const wKg = user.weight_kg;
              const hCm = user.height_cm;
              if (unitSystem === 'imperial') {
                setWeightInput(wKg ? String(Math.round(kgToLbs(wKg))) : '');
                setHeightInput(hCm ? String(Math.round(cmToIn(hCm))) : '');
              } else {
                setWeightInput(wKg ? String(Math.round(wKg)) : '');
                setHeightInput(hCm ? String(Math.round(hCm)) : '');
              }
              setShowBodyModal(true);
            }}
            activeOpacity={0.7}
          >
            <View style={styles.linkLeft}>
              <Icon name="human-male-height" size={18} color={colors.textSecondary} />
              <Text style={[styles.linkText, { color: colors.text }]}>
                {t('profile.bodyMeasurements')}
              </Text>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              {(user.weight_kg || user.height_cm) && (
                <Text style={{ fontSize: 13, color: colors.textTertiary }}>
                  {user.weight_kg ? displayWeight(user.weight_kg, unitSystem) : ''}
                  {user.weight_kg && user.height_cm ? ' / ' : ''}
                  {user.height_cm ? displayHeight(user.height_cm, unitSystem) : ''}
                </Text>
              )}
              <Icon name="chevron-right" size={16} color={colors.textTertiary} />
            </View>
          </TouchableOpacity>
          <TouchableOpacity
            style={[
              styles.linkItem,
              { backgroundColor: colors.card, borderColor: colors.border },
            ]}
            onPress={toggleTheme}
            activeOpacity={0.7}
          >
            <View style={styles.linkLeft}>
              <Icon
                name={themeMode === 'dark' ? 'weather-sunny' : 'weather-night'}
                size={18}
                color={colors.textSecondary}
              />
              <Text style={[styles.linkText, { color: colors.text }]}>
                {themeMode === 'dark' ? t('profile.lightMode') : t('profile.darkMode')}
              </Text>
            </View>
            <Icon name="chevron-right" size={16} color={colors.textTertiary} />
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.linkItem,
              { backgroundColor: colors.card, borderColor: colors.border },
            ]}
            activeOpacity={0.7}
            onPress={() => setShowLanguagePicker(true)}
          >
            <View style={styles.linkLeft}>
              <Icon name="translate" size={18} color={colors.textSecondary} />
              <Text style={[styles.linkText, { color: colors.text }]}>
                {t('profile.language')}
              </Text>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <Text style={{ fontSize: 13, color: colors.textTertiary }}>
                {SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language)?.label || 'English'}
              </Text>
              <Icon name="chevron-right" size={16} color={colors.textTertiary} />
            </View>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.linkItem,
              { backgroundColor: colors.card, borderColor: colors.border },
            ]}
            activeOpacity={0.7}
            onPress={() => setShowAppSettings(true)}
          >
            <View style={styles.linkLeft}>
              <Icon name="cog" size={18} color={colors.textSecondary} />
              <Text style={[styles.linkText, { color: colors.text }]}>
                {t('profile.appSettings')}
              </Text>
            </View>
            <Icon name="chevron-right" size={16} color={colors.textTertiary} />
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.linkItem,
              { backgroundColor: colors.card, borderColor: colors.border },
            ]}
            activeOpacity={0.7}
            onPress={() => setShowLegalDocuments(true)}
          >
            <View style={styles.linkLeft}>
              <Icon name="file-document-outline" size={18} color={colors.textSecondary} />
              <Text style={[styles.linkText, { color: colors.text }]}>
                {t('legal.profileSection')}
              </Text>
            </View>
            <Icon name="chevron-right" size={16} color={colors.textTertiary} />
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.linkItem,
              { backgroundColor: colors.card, borderColor: colors.border },
            ]}
            onPress={async () => {
              setIsLoggingOut(true);
              try {
                await logout();
              } finally {
                setIsLoggingOut(false);
              }
            }}
            activeOpacity={0.7}
            disabled={isLoggingOut}
          >
            <View style={styles.linkLeft}>
              {isLoggingOut ? (
                <ActivityIndicator size="small" color={colors.error} />
              ) : (
                <Icon name="logout" size={18} color={colors.error} />
              )}
              <Text style={[styles.linkText, { color: colors.error }]}>
                {isLoggingOut ? t('profile.signingOut') : t('profile.signOut')}
              </Text>
            </View>
            {isLoggingOut ? null : (
              <Icon name="chevron-right" size={16} color={colors.textTertiary} />
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* App Settings Modal */}
      <Modal
        visible={showAppSettings}
        transparent
        animationType="fade"
        onRequestClose={() => setShowAppSettings(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowAppSettings(false)}
        >
          <TouchableOpacity
            style={[styles.modalCard, { backgroundColor: colors.card, borderColor: colors.border }]}
            activeOpacity={1}
          >
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: colors.text }]}>{t('profile.appSettings')}</Text>
              <TouchableOpacity onPress={() => setShowAppSettings(false)}>
                <Icon name="close" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <View style={[styles.infoRow, { borderBottomColor: colors.border }]}>
              <Text style={[styles.infoLabel, { color: colors.textTertiary }]}>{t('profile.appName')}</Text>
              <Text style={[styles.infoValue, { color: colors.text }]}>
                {Constants.expoConfig?.name ?? 'CBJJ Saigon'}
              </Text>
            </View>
            <View style={[styles.infoRow, { borderBottomColor: colors.border }]}>
              <Text style={[styles.infoLabel, { color: colors.textTertiary }]}>{t('profile.version')}</Text>
              <Text style={[styles.infoValue, { color: colors.text }]}>
                {Constants.expoConfig?.version ?? '—'}
              </Text>
            </View>
            <View style={[styles.infoRow, { borderBottomColor: colors.border }]}>
              <Text style={[styles.infoLabel, { color: colors.textTertiary }]}>{t('profile.build')}</Text>
              <Text style={[styles.infoValue, { color: colors.text }]}>
                {/* Application.nativeBuildVersion reads the installed binary's
                    real build number (EAS assigns it remotely, so it's absent
                    from expoConfig — which showed "—"). */}
                {(Application.nativeBuildVersion
                  || Constants.nativeBuildVersion
                  || (Platform.OS === 'ios'
                    ? Constants.expoConfig?.ios?.buildNumber
                    : String(Constants.expoConfig?.android?.versionCode ?? ''))) || '—'}
              </Text>
            </View>
            <View style={[styles.infoRow, { borderBottomColor: colors.border }]}>
              <Text style={[styles.infoLabel, { color: colors.textTertiary }]}>{t('profile.platform')}</Text>
              <Text style={[styles.infoValue, { color: colors.text }]}>
                {(() => {
                  if (Platform.OS === 'ios') return `iOS ${Platform.Version || ''}`;
                  if (Platform.OS === 'android') return `Android ${Platform.Version || ''}`;
                  // Web: detect browser and OS from userAgent
                  if (Platform.OS === 'web' && typeof navigator !== 'undefined') {
                    const ua = navigator.userAgent;
                    let browser = 'Web';
                    let version = '';
                    if (ua.includes('Chrome') && !ua.includes('Edg')) {
                      browser = 'Chrome';
                      const match = ua.match(/Chrome\/(\d+\.\d+\.\d+\.\d+)/);
                      if (match) version = match[1];
                    } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
                      browser = 'Safari';
                      const match = ua.match(/Version\/(\d+\.\d+)/);
                      if (match) version = match[1];
                    } else if (ua.includes('Firefox')) {
                      browser = 'Firefox';
                      const match = ua.match(/Firefox\/(\d+\.\d+)/);
                      if (match) version = match[1];
                    } else if (ua.includes('Edg')) {
                      browser = 'Edge';
                      const match = ua.match(/Edg\/(\d+\.\d+\.\d+\.\d+)/);
                      if (match) version = match[1];
                    }
                    let os = '';
                    let osVersion = '';
                    if (ua.includes('Windows')) {
                      os = 'Windows';
                      const match = ua.match(/Windows NT (\d+\.\d+)/);
                      if (match) {
                        const ntVersion = match[1];
                        if (ntVersion === '10.0') osVersion = '10/11';
                        else if (ntVersion === '6.3') osVersion = '8.1';
                        else if (ntVersion === '6.2') osVersion = '8';
                        else if (ntVersion === '6.1') osVersion = '7';
                        else osVersion = ntVersion;
                      }
                    } else if (ua.includes('Mac')) {
                      os = 'macOS';
                      const match = ua.match(/Mac OS X (\d+[_\.]\d+[_\.]?\d*)/);
                      if (match) osVersion = match[1].replace(/_/g, '.');
                    } else if (ua.includes('Linux')) {
                      os = 'Linux';
                      // Linux distros vary, just show generic
                    }
                    const browserStr = version ? `${browser} ${version}` : browser;
                    const osStr = osVersion ? `${os} ${osVersion}` : os;
                    return osStr ? `${browserStr} on ${osStr}` : browserStr;
                  }
                  return 'Web';
                })()}
              </Text>
            </View>
            <View style={styles.infoRow}>
              <Text style={[styles.infoLabel, { color: colors.textTertiary }]}>{t('profile.bundleId')}</Text>
              <Text style={[styles.infoValue, { color: colors.text }]}>
                {(Platform.OS === 'ios'
                  ? Constants.expoConfig?.ios?.bundleIdentifier
                  : Constants.expoConfig?.android?.package) ?? '—'}
              </Text>
            </View>
            {Platform.OS !== 'web' && (
              <View style={[styles.infoRow, { borderBottomColor: colors.border }]}>
                <Text style={[styles.infoLabel, { color: colors.textTertiary }]}>{t('profile.channel')}</Text>
                <Text style={[styles.infoValue, { color: colors.text }]}>
                  {Updates.channel || t('profile.unknown')}
                </Text>
              </View>
            )}
            {Platform.OS !== 'web' && (() => {
              const fullId = Updates.updateId || null;
              const shortId = fullId ? fullId.replace(/-/g, '').slice(0, 8) : null;
              return (
                <View style={styles.infoRow}>
                  <Text style={[styles.infoLabel, { color: colors.textTertiary }]}>{t('profile.updateId')}</Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexShrink: 1 }}>
                    <Text style={[styles.infoValue, { color: colors.text }]}>
                      {shortId ?? t('profile.unknown')}
                    </Text>
                    {fullId && (
                      <TouchableOpacity
                        onPress={async () => {
                          await Clipboard.setStringAsync(fullId);
                        }}
                        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                      >
                        <Icon name="content-copy" size={14} color={colors.textTertiary} />
                      </TouchableOpacity>
                    )}
                  </View>
                </View>
              );
            })()}
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Legal Documents Modal */}
      <Modal
        visible={showLegalDocuments}
        transparent
        animationType="fade"
        onRequestClose={() => setShowLegalDocuments(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowLegalDocuments(false)}
        >
          <TouchableOpacity
            style={[styles.modalCard, { backgroundColor: colors.card, borderColor: colors.border, maxHeight: '80%' }]}
            activeOpacity={1}
          >
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: colors.text }]}>{t('legal.profileSection')}</Text>
              <TouchableOpacity onPress={() => setShowLegalDocuments(false)}>
                <Icon name="close" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
            <ScrollView showsVerticalScrollIndicator={false}>
              <LegalDocumentsSection
                familyMemberships={familyMemberships}
                currentUserId={userId}
              />
            </ScrollView>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Language Picker Modal */}
      <Modal
        visible={showLanguagePicker}
        transparent
        animationType="fade"
        onRequestClose={() => setShowLanguagePicker(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowLanguagePicker(false)}
        >
          <TouchableOpacity
            style={[styles.modalCard, { backgroundColor: colors.card, borderColor: colors.border }]}
            activeOpacity={1}
          >
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: colors.text }]}>{t('profile.language')}</Text>
              <TouchableOpacity onPress={() => setShowLanguagePicker(false)}>
                <Icon name="close" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
            {SUPPORTED_LANGUAGES.map((lang) => {
              const isActive = i18n.language === lang.code;
              return (
                <TouchableOpacity
                  key={lang.code}
                  style={[
                    styles.infoRow,
                    isActive && { backgroundColor: colors.border, borderRadius: 8 },
                  ]}
                  onPress={() => {
                    changeLanguage(lang.code);
                    setShowLanguagePicker(false);
                  }}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.infoLabel, { color: isActive ? colors.text : colors.textTertiary, fontWeight: isActive ? '700' : '400' }]}>
                    {lang.label}
                  </Text>
                  {isActive && <Icon name="check" size={18} color={colors.text} />}
                </TouchableOpacity>
              );
            })}
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Invite Sub-User Modal */}
      <Modal
        visible={!!inviteTarget}
        transparent
        animationType="fade"
        onRequestClose={closeInviteModal}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={closeInviteModal}
        >
          <TouchableOpacity
            style={[styles.modalCard, { backgroundColor: colors.card, borderColor: colors.border }]}
            activeOpacity={1}
          >
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: colors.text }]}>
                {t('profile.invite.title')}
              </Text>
              <TouchableOpacity onPress={closeInviteModal}>
                <Icon name="close" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            <Text style={[styles.inviteHelp, { color: colors.textSecondary }]}>
              {t('profile.invite.help', { name: inviteTarget?.name || '' })}
            </Text>

            {inviteSubUser.isPending ? (
              <View style={styles.inviteLoading}>
                <ActivityIndicator size="large" color={colors.accent} />
              </View>
            ) : inviteToken && inviteUrl ? (
              <View style={styles.inviteContent}>
                <View style={styles.qrContainer}>
                  <QRCode
                    value={inviteUrl}
                    size={200}
                    color={colors.text}
                    backgroundColor={colors.card}
                  />
                </View>

                <Text style={[styles.inviteLinkLabel, { color: colors.textSecondary }]}>
                  {t('profile.invite.linkLabel')}
                </Text>
                <View style={[styles.inviteLink, { borderColor: colors.border }]}>
                  <TouchableOpacity
                    onPress={() => Linking.openURL(inviteUrl)}
                    style={styles.inviteLinkOpen}
                    activeOpacity={0.7}
                  >
                    <Text
                      style={[styles.inviteLinkText, { color: colors.accent }]}
                      numberOfLines={1}
                      ellipsizeMode="middle"
                    >
                      {truncateUrl(inviteUrl)}
                    </Text>
                    <Icon name="open-in-new" size={16} color={colors.accent} />
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={copyInviteUrl}
                    style={[styles.inviteCopyBtn, { borderLeftColor: colors.border }]}
                    activeOpacity={0.7}
                    accessibilityLabel={t('profile.invite.copy')}
                  >
                    <Icon
                      name={inviteCopied ? 'check' : 'content-copy'}
                      size={16}
                      color={inviteCopied ? colors.success ?? colors.accent : colors.accent}
                    />
                  </TouchableOpacity>
                </View>
                {inviteCopied && (
                  <Text style={[styles.inviteCopiedText, { color: colors.textSecondary }]}>
                    {t('profile.invite.copied')}
                  </Text>
                )}

                {inviteExpires && (
                  <Text style={[styles.inviteExpires, { color: colors.textTertiary }]}>
                    {t('profile.invite.expires', { date: new Date(inviteExpires).toLocaleDateString() })}
                  </Text>
                )}

                <TouchableOpacity
                  style={[styles.inviteRegenerateBtn, { borderColor: colors.border }]}
                  onPress={regenerateInvite}
                  disabled={inviteSubUser.isPending}
                  activeOpacity={0.7}
                >
                  <Icon name="refresh" size={16} color={colors.textSecondary} />
                  <Text style={[styles.inviteRegenerateText, { color: colors.textSecondary }]}>
                    {t('profile.invite.regenerate')}
                  </Text>
                </TouchableOpacity>
              </View>
            ) : (
              <Text style={[styles.inviteError, { color: colors.error }]}>
                {inviteError || t('profile.invite.failed')}
              </Text>
            )}

            <View style={styles.inviteButtons}>
              <TouchableOpacity
                style={[styles.inviteCancelBtn, { borderColor: colors.border }]}
                onPress={closeInviteModal}
                activeOpacity={0.7}
              >
                <Text style={[styles.inviteCancelBtnText, { color: colors.textSecondary }]}>
                  {t('common.close')}
                </Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Body Measurements Modal */}
      <Modal
        visible={showBodyModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowBodyModal(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowBodyModal(false)}
        >
          <TouchableOpacity
            style={[styles.modalCard, { backgroundColor: colors.card, borderColor: colors.border }]}
            activeOpacity={1}
          >
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: colors.text }]}>{t('profile.bodyMeasurements')}</Text>
              <TouchableOpacity onPress={() => setShowBodyModal(false)}>
                <Icon name="close" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>

            {/* Unit toggle */}
            <View style={styles.unitToggleRow}>
              <TouchableOpacity
                style={[
                  styles.unitToggleBtn,
                  unitSystem === 'metric' && { backgroundColor: colors.accent },
                ]}
                onPress={() => {
                  if (unitSystem === 'imperial') {
                    const w = parseFloat(weightInput);
                    const h = parseFloat(heightInput);
                    setWeightInput(!isNaN(w) ? String(Math.round(lbsToKg(w))) : '');
                    setHeightInput(!isNaN(h) ? String(Math.round(inToCm(h))) : '');
                    setUnitSystem('metric');
                  }
                }}
                activeOpacity={0.7}
              >
                <Text style={[styles.unitToggleText, { color: unitSystem === 'metric' ? colors.accentText : colors.textSecondary }]}>
                  {t('profile.metric')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.unitToggleBtn,
                  unitSystem === 'imperial' && { backgroundColor: colors.accent },
                ]}
                onPress={() => {
                  if (unitSystem === 'metric') {
                    const w = parseFloat(weightInput);
                    const h = parseFloat(heightInput);
                    setWeightInput(!isNaN(w) ? String(Math.round(kgToLbs(w))) : '');
                    setHeightInput(!isNaN(h) ? String(Math.round(cmToIn(h))) : '');
                    setUnitSystem('imperial');
                  }
                }}
                activeOpacity={0.7}
              >
                <Text style={[styles.unitToggleText, { color: unitSystem === 'imperial' ? colors.accentText : colors.textSecondary }]}>
                  {t('profile.imperial')}
                </Text>
              </TouchableOpacity>
            </View>

            <View style={styles.bodyEditContainer}>
              <View style={styles.bodyEditRow}>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.bodyLabel, { color: colors.textSecondary }]}>
                    {t('profile.weight')}
                  </Text>
                  <View style={styles.bodyInputRow}>
                    <TextInput
                      style={[styles.bodyInput, { backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }]}
                      value={weightInput}
                      onChangeText={setWeightInput}
                      keyboardType="decimal-pad"
                      placeholder={unitSystem === 'metric' ? 'kg' : 'lbs'}
                      placeholderTextColor={colors.textTertiary}
                    />
                    <Text style={[styles.bodyUnit, { color: colors.textTertiary }]}>
                      {unitSystem === 'metric' ? 'kg' : 'lbs'}
                    </Text>
                  </View>
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.bodyLabel, { color: colors.textSecondary }]}>
                    {t('profile.height')}
                  </Text>
                  <View style={styles.bodyInputRow}>
                    <TextInput
                      style={[styles.bodyInput, { backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }]}
                      value={heightInput}
                      onChangeText={setHeightInput}
                      keyboardType="decimal-pad"
                      placeholder={unitSystem === 'metric' ? 'cm' : 'in'}
                      placeholderTextColor={colors.textTertiary}
                    />
                    <Text style={[styles.bodyUnit, { color: colors.textTertiary }]}>
                      {unitSystem === 'metric' ? 'cm' : 'in'}
                    </Text>
                  </View>
                </View>
              </View>
              <View style={styles.bodyEditActions}>
                <TouchableOpacity
                  style={[styles.bodyActionBtn, { backgroundColor: colors.accent, flex: 1 }]}
                  onPress={() => {
                    const wVal = parseFloat(weightInput);
                    const hVal = parseFloat(heightInput);
                    const weightKg = !isNaN(wVal) ? weightToKg(wVal, unitSystem) : null;
                    const heightCm = !isNaN(hVal) ? heightToCm(hVal, unitSystem) : null;
                    updateProfile.mutate({
                      weight_kg: weightKg,
                      height_cm: heightCm,
                      measurement_unit: unitSystem,
                    });
                    setShowBodyModal(false);
                  }}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.bodyActionText, { color: colors.accentText }]}>{t('common.save')}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.bodyActionBtn, { borderColor: colors.border, borderWidth: 1, flex: 1 }]}
                  onPress={() => setShowBodyModal(false)}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.bodyActionText, { color: colors.textSecondary }]}>{t('common.cancel')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Profile Menu Modal */}
      <Modal
        visible={showProfileMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setShowProfileMenu(false)}
        onDismiss={runPendingProfileMenuAction}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowProfileMenu(false)}
        >
          <TouchableOpacity
            style={[styles.modalCard, { backgroundColor: colors.card, borderColor: colors.border }]}
            activeOpacity={1}
          >
            <View style={styles.menuOptions}>
              <TouchableOpacity
                style={styles.menuOption}
                onPress={() => {
                  setPersonalFirst(user.first_name || '');
                  setPersonalLast(user.last_name || '');
                  // Parse YYYY-MM-DD as a LOCAL date — new Date('YYYY-MM-DD')
                  // is UTC and shifts back a day west of UTC.
                  setPersonalDob(user.dob ? (() => { const [y, m, d] = user.dob.split('-').map(Number); return new Date(y, m - 1, d); })() : null);
                  setPersonalError('');
                  // Open the editor only after this menu has fully dismissed (iOS)
                  pendingProfileMenuAction.current = () => setShowPersonalModal(true);
                  setShowProfileMenu(false);
                  if (Platform.OS !== 'ios') runPendingProfileMenuAction();
                }}
                activeOpacity={0.7}
              >
                <Icon name="account-edit-outline" size={20} color={colors.text} />
                <Text style={[styles.menuOptionText, { color: colors.text }]}>
                  {t('profile.personal.edit')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.menuOption}
                onPress={() => {
                  setShowProfileMenu(false);
                  navigation.navigate('AccountDeletion');
                }}
                activeOpacity={0.7}
              >
                <Icon name="account-remove-outline" size={20} color={colors.error} />
                <Text style={[styles.menuOptionText, { color: colors.error }]}>
                  {t('accountDeletion.link')}
                </Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Personal Details Edit Modal */}
      <Modal
        visible={showPersonalModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowPersonalModal(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowPersonalModal(false)}
        >
          <TouchableOpacity
            style={[styles.modalCard, { backgroundColor: colors.card, borderColor: colors.border }]}
            activeOpacity={1}
          >
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: colors.text }]}>
                {t('profile.personal.title')}
              </Text>
              <TouchableOpacity onPress={() => setShowPersonalModal(false)}>
                <Icon name="close" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
            <View style={styles.bodyEditContainer}>
              <View style={styles.bodyEditRow}>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.bodyLabel, { color: colors.textSecondary }]}>
                    {t('profile.personal.firstName')}
                  </Text>
                  <TextInput
                    style={[styles.bodyInput, { backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }]}
                    value={personalFirst}
                    onChangeText={(v) => { setPersonalFirst(v); if (personalError) setPersonalError(''); }}
                  />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[styles.bodyLabel, { color: colors.textSecondary }]}>
                    {t('profile.personal.lastName')}
                  </Text>
                  <TextInput
                    style={[styles.bodyInput, { backgroundColor: colors.bg, color: colors.text, borderColor: colors.border }]}
                    value={personalLast}
                    onChangeText={(v) => { setPersonalLast(v); if (personalError) setPersonalError(''); }}
                  />
                </View>
              </View>
              <DatePicker
                label={t('profile.personal.birthday')}
                value={personalDob}
                onChange={(d) => { setPersonalDob(d); if (personalError) setPersonalError(''); }}
                maximumDate={new Date()}
              />
              {!!personalError && (
                <Text style={{ color: colors.error, fontSize: 13, textAlign: 'center', marginBottom: 8 }}>
                  {personalError}
                </Text>
              )}
              <View style={styles.bodyEditActions}>
                <TouchableOpacity
                  style={[styles.bodyActionBtn, { backgroundColor: colors.accent, flex: 1 }]}
                  onPress={() => {
                    const first = personalFirst.trim();
                    const last = personalLast.trim();
                    if (!first || !last) {
                      setPersonalError(t('profile.personal.nameRequired'));
                      return;
                    }
                    // Main accounts must stay above the kids-plan age — mirror
                    // the backend rule (child = age <= 15), not the 18+ signup
                    // gate, so legitimate 16-17 year old members aren't blocked.
                    if (user.email && personalDob) {
                      const now = new Date();
                      let age = now.getFullYear() - personalDob.getFullYear();
                      const md = now.getMonth() - personalDob.getMonth();
                      if (md < 0 || (md === 0 && now.getDate() < personalDob.getDate())) age--;
                      if (age <= 15) {
                        setPersonalError(t('auth.ageRequirement'));
                        return;
                      }
                    }
                    const dobStr = personalDob
                      ? `${personalDob.getFullYear()}-${String(personalDob.getMonth() + 1).padStart(2, '0')}-${String(personalDob.getDate()).padStart(2, '0')}`
                      : undefined;
                    updateProfile.mutate(
                      { first_name: first, last_name: last, ...(dobStr ? { dob: dobStr } : {}) },
                      {
                        onSuccess: () => setShowPersonalModal(false),
                        onError: (err: any) => setPersonalError(getApiError(err, t('common.error'), t)),
                      },
                    );
                  }}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.bodyActionText, { color: colors.accentText }]}>
                    {updateProfile.isPending ? t('common.loading') : t('profile.personal.save')}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Profile Picture Menu Modal */}
      <Modal
        visible={showProfilePictureMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setShowProfilePictureMenu(false)}
        onDismiss={runPendingPictureAction}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowProfilePictureMenu(false)}
        >
          <TouchableOpacity
            style={[styles.modalCard, { backgroundColor: colors.card, borderColor: colors.border } ]}
            activeOpacity={1}
          >
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: colors.text }]}>
                {t('profile.picture.title')}
              </Text>
              <TouchableOpacity onPress={() => setShowProfilePictureMenu(false)}>
                <Icon name="close" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
            <View style={styles.menuOptions}>
              <TouchableOpacity
                style={styles.menuOption}
                onPress={() => {
                  // Defer the picker until the modal has fully dismissed (iOS)
                  pendingPictureAction.current = () => {
                    pickAndUploadImage().catch(() => Alert.alert(t('common.error'), t('profile.picture.uploadFailed', 'Could not upload the photo. Please try again.')));
                  };
                  setShowProfilePictureMenu(false);
                  if (Platform.OS !== 'ios') runPendingPictureAction();
                }}
                activeOpacity={0.7}
              >
                <Icon name="camera" size={20} color={colors.text} />
                <Text style={[styles.menuOptionText, { color: colors.text }]}>
                  {t('profile.picture.upload')}
                </Text>
              </TouchableOpacity>
              {user.profile_picture_url && (
                <TouchableOpacity
                  style={styles.menuOption}
                  onPress={() => {
                    pendingPictureAction.current = () => {
                      deleteImage().catch(() => Alert.alert(t('common.error'), t('profile.picture.deleteFailed', 'Could not remove the photo. Please try again.')));
                    };
                    setShowProfilePictureMenu(false);
                    if (Platform.OS !== 'ios') runPendingPictureAction();
                  }}
                  activeOpacity={0.7}
                >
                  <Icon name="delete" size={20} color={colors.error} />
                  <Text style={[styles.menuOptionText, { color: colors.error }]}>
                    {t('profile.picture.remove')}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1 },
  scroll: { flex: 1 },
  content: { padding: 24, paddingBottom: 120, gap: 24 },

  // Profile info
  profileRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  avatarWrapper: {
    width: 64,
    height: 64,
    position: 'relative',
  },
  avatar: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    overflow: 'hidden',
  },
  avatarImage: {
    width: 64,
    height: 64,
    borderRadius: 32,
  },
  avatarOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 32,
  },
  cameraIconOverlay: {
    position: 'absolute',
    bottom: -4,
    right: -4,
    backgroundColor: 'rgba(0,0,0,0.6)',
    borderRadius: 10,
    padding: 4,
  },
  avatarEmoji: {
    fontSize: 28,
  },
  profileInfo: {
    flex: 1,
    gap: 2,
  },
  profileName: {
    fontSize: 20,
    fontWeight: '700',
  },
  profileSub: {
    fontSize: 14,
  },
  profileDob: {
    fontSize: 12,
    marginTop: 2,
  },

  // Section
  section: {
    gap: 12,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },

  // Family Members
  familyCard: {
    borderRadius: 12,
    borderWidth: 1,
    marginTop: 8,
    overflow: 'hidden',
  },
  memberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    gap: 12,
  },
  memberDivider: {
    borderBottomWidth: 1,
  },
  childAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  childInfo: {
    flex: 1,
    gap: 2,
  },
  childName: {
    fontSize: 14,
    fontWeight: '600',
  },
  childMeta: {
    fontSize: 12,
  },
  viewingBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  viewingBadgeText: {
    fontSize: 10,
    fontWeight: '600',
  },
  guardianBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  guardianBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
  },

  // Paused badge for family members
  pausedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    borderWidth: 1,
  },
  pausedBadgeText: {
    fontSize: 10,
    fontWeight: '600',
  },

  // Membership Summary Card
  summaryCard: {
    borderRadius: 12,
    borderWidth: 1,
    marginTop: 8,
    padding: 16,
    gap: 0,
  },
  pendingActivationBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 10,
    borderWidth: 1,
    padding: 12,
    marginBottom: 12,
  },
  pendingActivationTitle: {
    fontSize: 13,
    fontWeight: '700',
  },
  pendingActivationBody: {
    fontSize: 12,
    lineHeight: 18,
    marginTop: 2,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  summaryHeaderRow: {
    paddingBottom: 4,
  },
  summaryLabel: {
    fontSize: 13,
  },
  summaryPlanName: {
    fontSize: 15,
  },
  summaryValue: {
    fontSize: 13,
  },
  totalLabel: {
    fontSize: 14,
    fontWeight: '700',
  },
  totalValue: {
    fontSize: 14,
    fontWeight: '700',
  },
  summaryMetaValue: {
    fontSize: 12,
    fontWeight: '500',
  },
  summaryDiscount: {
    fontSize: 11,
    fontWeight: '500',
    marginTop: 2,
  },
  summaryStatus: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  summaryDivider: {
    borderBottomWidth: 1,
  },
  planMeta: {
    alignItems: 'flex-end',
    gap: 3,
    marginLeft: 12,
  },
  priceCol: {
    alignItems: 'flex-end',
  },
  priceStrike: {
    fontSize: 11,
    textDecorationLine: 'line-through',
  },
  familyMembershipRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 10,
  },
  activeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  activeDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  manageBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 12,
    paddingVertical: 12,
    borderRadius: 10,
  },
  manageBtnText: {
    fontSize: 14,
    fontWeight: '600',
  },

  // Links
  linkList: {
    gap: 8,
  },
  linkItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  linkLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  linkText: {
    fontSize: 14,
    fontWeight: '600',
  },

  // App Settings Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalCard: {
    width: '100%',
    maxWidth: WEB_MAX_WIDTH,
    borderRadius: 16,
    borderWidth: 1,
    padding: 20,
    gap: 0,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 17,
    fontWeight: '700',
  },
  menuOptions: {
    gap: 8,
  },
  menuOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderRadius: 8,
  },
  menuOptionText: {
    fontSize: 15,
    fontWeight: '500',
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  infoLabel: {
    fontSize: 13,
  },
  infoValue: {
    fontSize: 13,
    fontWeight: '600',
    textAlign: 'right',
    flex: 1,
    marginLeft: 12,
  },

  // Managed-by banner (sub-user)
  managedBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
  },
  managedBannerText: {
    fontSize: 13,
    flex: 1,
  },

  // Invite-to-app button in family member rows
  inviteBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
  },
  inviteBtnText: {
    fontSize: 12,
    fontWeight: '600',
  },

  // Invite modal
  inviteHelp: {
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 16,
  },
  inviteLoading: {
    paddingVertical: 32,
    alignItems: 'center',
  },
  inviteContent: {
    alignItems: 'center',
    gap: 16,
  },
  qrContainer: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#ccc',
  },
  inviteLinkLabel: {
    fontSize: 12,
    fontWeight: '600',
  },
  inviteLink: {
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    borderRadius: 8,
    borderWidth: 1,
    width: '100%',
    overflow: 'hidden',
  },
  inviteLinkText: {
    fontSize: 12,
    flex: 1,
    marginRight: 8,
  },
  inviteLinkOpen: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  inviteCopyBtn: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderLeftWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  inviteCopiedText: {
    fontSize: 11,
    textAlign: 'center',
    marginTop: -8,
  },
  inviteExpires: {
    fontSize: 11,
    textAlign: 'center',
  },
  inviteRegenerateBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  inviteRegenerateText: {
    fontSize: 13,
  },
  inviteError: {
    fontSize: 13,
    textAlign: 'center',
    color: '#ef4444',
    paddingVertical: 16,
  },
  inviteButtons: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 16,
  },
  inviteCancelBtn: {
    flex: 1,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
  },
  inviteCancelBtnText: {
    fontSize: 14,
    fontWeight: '500',
  },

  // Referral Section
  referralCard: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    gap: 12,
  },
  referralHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  referralTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  referralDesc: {
    fontSize: 13,
    lineHeight: 18,
  },
  codeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
  },
  codePill: {
    flex: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center',
  },
  codeText: {
    fontSize: 16,
    fontWeight: '700',
    letterSpacing: 1,
  },
  codeActionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
  },
  codeActionText: {
    fontSize: 13,
    fontWeight: '600',
  },
  statusText: {
    fontSize: 12,
    fontStyle: 'italic',
    textAlign: 'center',
  },
  statusBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  statusBoxText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
  },
  refereeBox: {
    marginTop: 8,
    paddingTop: 12,
    borderTopWidth: 1,
    gap: 8,
  },
  refereeTitle: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },

  // Multi-use referral styles
  referralStats: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    padding: 12,
    borderRadius: 8,
    marginTop: 4,
  },
  referralStat: {
    alignItems: 'center',
    flex: 1,
  },
  referralStatValue: {
    fontSize: 18,
    fontWeight: '700',
  },
  referralStatLabel: {
    fontSize: 11,
    marginTop: 2,
  },
  referralStatDivider: {
    width: 1,
    height: 24,
    backgroundColor: '#ccc',
  },
  revokedBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 8,
    marginTop: 4,
  },
  revokedText: {
    fontSize: 13,
    fontWeight: '500',
  },
  redemptionsList: {
    marginTop: 8,
  },
  redemptionsTitle: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  redemptionItem: {
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  redemptionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  redemptionName: {
    fontSize: 14,
    fontWeight: '500',
    flex: 1,
  },
  rewardBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  rewardBadgeText: {
    fontSize: 11,
    fontWeight: '600',
  },
  redemptionDate: {
    fontSize: 12,
  },

  // Body Measurements
  unitToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  unitToggleBtn: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  unitToggleText: {
    fontSize: 12,
    fontWeight: '600',
  },
  bodyEditContainer: {
    gap: 12,
  },
  bodyEditRow: {
    flexDirection: 'row',
    gap: 12,
  },
  bodyLabel: {
    fontSize: 12,
    fontWeight: '500',
    marginBottom: 4,
  },
  bodyInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  bodyInput: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    fontSize: 14,
  },
  bodyUnit: {
    fontSize: 12,
    fontWeight: '500',
    width: 24,
  },
  bodyEditActions: {
    flexDirection: 'row',
    gap: 8,
  },
  bodyActionBtn: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    alignItems: 'center',
  },
  bodyActionText: {
    fontSize: 13,
    fontWeight: '600',
  },
  bodyDisplayContainer: {
    paddingVertical: 4,
  },
  bodyDisplayRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  bodyValue: {
    fontSize: 15,
    fontWeight: '600',
  },
});
